import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { clearPermissionsCache } from '../hooks/useAdminPermissions';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser]             = useState(null);
  const [profile, setProfile]       = useState(null);
  const [isAdmin, setIsAdmin]       = useState(false);
  const [isVendor, setIsVendor]     = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  // An agent sees ONLY the agent portal. Their users row still says 'customer'
  // so RLS on their own data keeps working, but the app treats agent as an
  // exclusive role — no customer pages, no customer nav.
  const [isAgent, setIsAgent] = useState(false);
  const [loading, setLoading]       = useState(true);

  const fetchingRef = useRef(false);
  // Who we have already read from the database. Used to tell "the session was
  // renewed" apart from "a different person signed in".
  const loadedUserIdRef = useRef(null);
  // Once the app has painted, it must never blank again. Later reloads of the
  // profile happen silently underneath whatever the person is looking at.
  const paintedRef = useRef(false);

  const resetAuthState = () => {
    setProfile(null);
    setIsAdmin(false);
    setIsVendor(false);
    setIsCustomer(false);
    setIsAgent(false);
  };

  const fetchProfile = useCallback(async (authUser, options) => {
    const silent = options && options.silent;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!silent) setLoading(true);

    try {
      // ── 1. Admin check ──────────────────────────────────────────
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (adminData) {
        setProfile(adminData);
        setIsAdmin(true);
        setIsVendor(false);
        setIsCustomer(false);
        return;
      }

      // ── 2. Regular user (customer or vendor) ────────────────────
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (userData) {
        // ── REDEEM A REFERRAL CODE ON FIRST SIGN-IN ──
        // Runs once: referred_by is set for life by the RPC, and one referrer
        // per customer is enforced in the database, so a repeat call is a
        // harmless no-op rather than a second reward.
        const pendingCode = authUser?.user_metadata?.referral_code;
        if (pendingCode && !userData.referred_by) {
          try {
            await supabase.rpc('apply_referral_code', {
              p_code: String(pendingCode).trim().toUpperCase(),
              p_customer_id: authUser.id,
            });
          } catch (e) {
            // An invalid or expired code must never block sign-in.
            console.error('referral code:', e);
          }
        }

        setProfile(userData);
        setIsAdmin(false);

        // One lightweight lookup; RLS lets a person read their own agent row.
        let agentActive = false;
        try {
          const { data: agentRow } = await supabase
            .from('agents').select('id, active').eq('id', authUser.id).maybeSingle();
          agentActive = Boolean(agentRow?.active);
        } catch (e) { agentActive = false; }
        setIsAgent(agentActive);

        const isVendorUser =
          userData.account_type === 'vendor' || userData.role === 'vendor';
        setIsVendor(isVendorUser);
        // Exclusive: an active agent is never treated as a customer.
        setIsCustomer(!isVendorUser && !agentActive);
        return;
      }

      // ── 3. No DB row found ──────────────────────────────────────
      // Fall back to user_metadata so redirect still works even if
      // RLS is blocking the users table read
      const meta = authUser.user_metadata || {};
      const isVendorMeta = meta.account_type === 'vendor';
      setProfile(null);
      setIsAdmin(false);
      setIsVendor(isVendorMeta);
      setIsCustomer(!isVendorMeta);

    } catch (err) {
      console.error('fetchProfile error:', err);
      // Even on error — default to customer so the app is usable
      setIsAdmin(false);
      setIsVendor(false);
      setIsCustomer(true);
    } finally {
      fetchingRef.current = false;
      loadedUserIdRef.current = authUser.id;
      paintedRef.current = true;
      if (!silent) setLoading(false);

      // LAST SEEN. users.last_active existed for months with nothing writing to
      // it, so every roster read "Never" regardless of how active an account
      // was. This is the only writer.
      //
      // Not awaited and never surfaced: it is a statistic, and a failed write
      // must not delay a sign-in or show an error for something the person did
      // not ask for. The RPC throttles itself to one write an hour, so calling
      // it on every session resolution — including token refreshes — costs
      // almost nothing.
      // Promise.resolve() because supabase-js v2 returns a query BUILDER, not a
      // Promise. It is thenable, so `await` works, but it has no .catch() — and
      // calling one throws a TypeError at runtime rather than at build time.
      Promise.resolve(supabase.rpc('touch_last_active')).catch(() => {});
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        // The one and only time the loading screen is allowed to show.
        fetchProfile(currentUser);
      } else {
        resetAuthState();
        paintedRef.current = true;
        setLoading(false);
      }
    }).catch(() => {
      resetAuthState();
      paintedRef.current = true;
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const currentUser = session?.user ?? null;

        // Supabase renews the access token whenever the tab regains focus and
        // on a timer. That says the session is still valid — not that anyone
        // changed. Re-reading the profile here is what used to blank the whole
        // app every time you switched windows and came back.
        if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          if (currentUser) setUser(currentUser);
          return;
        }

        if (!currentUser) {
          loadedUserIdRef.current = null;
          setUser(null);
          resetAuthState();
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Same person we already have loaded — nothing to read again.
        if (loadedUserIdRef.current === currentUser.id) return;

        // A genuinely different person signed in. Read their profile, but only
        // show the loading screen if the app hasn't painted anything yet.
        fetchingRef.current = false;
        fetchProfile(currentUser, { silent: paintedRef.current });
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signOut = async () => {
    await supabase.auth.signOut();
    loadedUserIdRef.current = null;
    // The admin permission cache is module-level and survives a route change,
    // so it has to be cleared here explicitly. Without this the next person to
    // sign in on the same tab inherits the previous admin's permissions.
    clearPermissionsCache();
    resetAuthState();
    setUser(null);
  };

  // Called by pages after they change something on the profile. Always silent:
  // a manual refresh should never tear the screen down.
  const refreshProfile = async () => {
    if (user) {
      fetchingRef.current = false;
      await fetchProfile(user, { silent: true });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      isAdmin,
      isVendor,
      isCustomer,
      isAgent,
      loading,
      signOut,
      fetchProfile: refreshProfile,
      accountType: isAdmin ? 'admin' : isVendor ? 'vendor' : isAgent ? 'agent' : 'customer',
      role: isAdmin ? 'admin' : isVendor ? 'vendor' : isAgent ? 'agent' : 'customer',
    }}>
      {children}
    </AuthContext.Provider>
  );
};